import { useState } from "react";
import {
    Card,
    CardActionArea,
    CardMedia,
    Divider,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, Box } from "@mui/system";
import { ExpandLess, ExpandMore, MoreVert } from "@mui/icons-material";

/**
 * Cartão hierárquico — paridade visual com clientesSwap.
 *
 * Props:
 *  - title           : texto principal
 *  - subtitle        : texto secundário (opcional)
 *  - selected        : item ativo
 *  - childSelected   : algum descendente ativo
 *  - onClick         : clique no corpo
 *  - hasChildren     : exibe toggle de expand à esquerda
 *  - isExpanded      : estado de expansão
 *  - onToggleExpand  : toggle do expand
 *  - isChild         : aplica recuo `ml: 1`
 *  - actions         : [{ label, icon, onClick, divider, color }]
 */
export default function CardSelection({
    title,
    subtitle,
    selected = false,
    childSelected = false,
    onClick,
    hasChildren = false,
    isExpanded = false,
    onToggleExpand,
    isChild = false,
    actions = [],
    metrics = [],
    accentColor,
    accentSoftColor,
}) {
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = useState(null);

    const accent = accentColor || theme.palette.colors.blue.light;
    const accentSoft =
        accentSoftColor ||
        theme.palette.colors.blue.medium ||
        theme.palette.colors.blue.light;

    const handleOpenMenu = (event) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
    };
    const handleCloseMenu = (event) => {
        event?.stopPropagation?.();
        setMenuAnchor(null);
    };

    const showMenu = actions.length > 0;

    return (
        <Grid sx={{ mt: 0.5, ml: isChild ? 1 : 0 }}>
            <Card variant="outlined">
                <Box
                    data-active={selected ? "" : undefined}
                    data-child-active={
                        childSelected && !selected ? "" : undefined
                    }
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "stretch",
                        border: selected ? `2px solid ${accent}` : "none",
                        position: "relative",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "5px",
                            height: "100%",
                            bgcolor: accent,
                        },
                        "&[data-active]": {
                            backgroundColor: "action.selected",
                            "&:hover": {
                                backgroundColor: "action.selectedHover",
                            },
                        },
                        "&[data-child-active]": {
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(accent, 0.2)
                                    : alpha(accentSoft, 0.1),
                            "&:hover": {
                                backgroundColor: "action.selectedHover",
                            },
                        },
                    }}
                >
                    {hasChildren && (
                        <CardMedia
                            sx={{
                                width: "auto",
                                minWidth: "40px",
                                display: "flex",
                                borderLeft: "none",
                                position: "relative",
                            }}
                        >
                            <CardActionArea
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleExpand?.(e);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                size="small"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                }}
                            >
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </CardActionArea>
                        </CardMedia>
                    )}

                    <CardActionArea
                        data-active={selected ? "" : undefined}
                        sx={{
                            paddingY: 1.5,
                            paddingX: !hasChildren ? 1.5 : 0,
                        }}
                        onClick={onClick}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            <Box sx={{ p: 0.5, flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                    }}
                                    component="span"
                                >
                                    {title}
                                </Typography>
                                {subtitle != null && subtitle !== "" && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                color: "text.secondary",
                                            }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {metrics.length > 0 && (
                                <Box
                                    sx={{
                                        display: { xs: "none", sm: "flex" },
                                        alignItems: "center",
                                        gap: 0.75,
                                        pr: 0.5,
                                        flexShrink: 0,
                                    }}
                                >
                                    {metrics.map((m, i) => (
                                        <Tooltip
                                            key={`metric-${i}`}
                                            title={m.tooltip || ""}
                                            arrow
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.25,
                                                    color:
                                                        m.color ||
                                                        "text.disabled",
                                                }}
                                            >
                                                {m.icon}
                                                {m.value !== undefined && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 600,
                                                            minWidth: 10,
                                                            textAlign: "left",
                                                        }}
                                                    >
                                                        {m.value}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </CardActionArea>

                    {showMenu && (
                        <CardMedia
                            sx={{
                                width: "auto",
                                minWidth: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderLeft: "none",
                            }}
                        >
                            <Tooltip title="Mais ações">
                                <IconButton
                                    size="small"
                                    onClick={handleOpenMenu}
                                >
                                    <MoreVert fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleCloseMenu}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {actions.flatMap((a, i) => {
                                    const item = (
                                        <MenuItem
                                            key={`mi-${i}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMenuAnchor(null);
                                                a.onClick?.();
                                            }}
                                            sx={
                                                a.color === "error"
                                                    ? {
                                                        color: "error.main",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                alpha(
                                                                    theme
                                                                        .palette
                                                                        .error
                                                                        .main,
                                                                    0.08
                                                                ),
                                                        },
                                                    }
                                                    : undefined
                                            }
                                        >
                                            {a.icon && (
                                                <ListItemIcon>
                                                    {a.icon}
                                                </ListItemIcon>
                                            )}
                                            <ListItemText primary={a.label} />
                                        </MenuItem>
                                    );
                                    return a.divider
                                        ? [item, <Divider key={`dv-${i}`} />]
                                        : [item];
                                })}
                            </Menu>
                        </CardMedia>
                    )}
                </Box>
            </Card>
        </Grid>
    );
}
